import { useContext, useEffect, useState } from "react";
import { RootContext } from "../../../../stores";
import { AppError } from "../../../../stores/models/errors/AppError";
import { IOrganizationSignUpFlowValues } from "../_interfaces/IOrganizationSignUpFlowValues";
import { ExistingAccountConfirmationStepValues } from "../_interfaces/IOrganizationSignUpStepTypeValues";
import { IVerifyCorrectAccountFormValues } from "../../../authentication/_interfaces/IVerifyCorrectAccountFormValues";

export function useExistingAccountConfirmationController(
    values: ExistingAccountConfirmationStepValues,
    allValues: IOrganizationSignUpFlowValues,
    update: (patch: Partial<ExistingAccountConfirmationStepValues>) => void,
    onSuccess: (override?: Partial<ExistingAccountConfirmationStepValues>) => void
): {
    values: IVerifyCorrectAccountFormValues,
    onChange: (patch: Partial<IVerifyCorrectAccountFormValues>) => void,
    onSubmit: (override?: Partial<ExistingAccountConfirmationStepValues>) => void,
    isSubmitting: boolean,
    error: string | undefined,
    fieldErrors: Partial<Record<keyof IVerifyCorrectAccountFormValues, string>>
} {
    const rootStore = useContext(RootContext);
    const userStore = rootStore.userStore;

    const [error, setError] = useState<string>();
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof IVerifyCorrectAccountFormValues, string>>>({});
    const [isSubmitting, setSubmitting] = useState(false);
    const [validationRun, setValidationRun] = useState(false);

    useEffect(() => {
        update({ backClicked: false, correctAccount: undefined })
    }, [])

    const isValidSubmission = (override?: Partial<ExistingAccountConfirmationStepValues>) => {
        return values.correctAccount !== undefined || override?.correctAccount !== undefined;
    }

    const submit = async (override?: Partial<ExistingAccountConfirmationStepValues>) => {
        setSubmitting(true);
        setError(undefined);
        setValidationRun(true);

        try {
            const shouldSwitchStepsWithoutValidation = values.backClicked || override?.backClicked;
            if (shouldSwitchStepsWithoutValidation) { // If the flow should move to a different step
                update({ ...override });
                onSuccess(override);
            } else if (isValidSubmission(override)) {
                const shouldLogout = (values.correctAccount !== undefined && !values.correctAccount) || (override?.correctAccount !== undefined && !override?.correctAccount);
                if (shouldLogout) {
                    update({ loading: true });
                    const result = await rootStore.userStore.logout();
                    const patch = {
                        currentUserEmail: userStore.user.email,
                        currentUserName: userStore.user.name,
                        isAuthenticated: false,
                        hasExistingOrganizationProfile: false,
                        correctAccount: false
                    };
                    update({ loading: false });
                    update(patch);
                    onSuccess(patch);
                } else {
                    update({ ...override });
                    onSuccess(override);
                }
            }
        } catch (err) {
            if (err instanceof AppError) {
                switch (err.code) {
                    default:
                        setError(err.message);
                }
            } else {
                setError("An unknown error occurred.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return {
        values: {
            ...values,
            email: allValues.currentUserEmail,
            userName: allValues.currentUserName,
            prompt: "Would you like to continue with this account?"
        },
        onChange: update,
        onSubmit: submit,
        isSubmitting,
        error,
        fieldErrors
    };
}